"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FelaCacheProvider = void 0;
exports.combineRulesWithName = combineRulesWithName;
exports.useFelaCache = useFelaCache;
const jsx_runtime_1 = require("react/jsx-runtime");
const ObjectUtils_1 = require("@gt/common-utils/build/datatypes/ObjectUtils");
const fela_1 = require("fela");
const react_1 = require("react");
const react_fela_1 = require("react-fela");
const felaCacheContext = (0, react_1.createContext)({
    cache: {},
    themeHash: "__unset__",
});
const Provider = felaCacheContext.Provider;
const isDev = process.env.NODE_ENV !== "production";
function combineRulesWithName(rules, name) {
    const combined = (0, fela_1.combineRules)(...rules);
    Object.defineProperty(combined, "name", { writable: true });
    combined.name = name;
    return combined;
}
const FelaCacheProvider = ({ children, themeHash, enabled = true, }) => {
    const { renderer, css, theme } = (0, react_fela_1.useFela)();
    const cache = (0, react_1.useRef)();
    function makeKey(rule, props, key) {
        if (isDev) {
            if (rule.name === "anonymous" || rule.name === "") {
                console.error(`Fela Cache: incoming rule function has no name - can't create cache correctly.`);
            }
        }
        if (!key) {
            key =
                "_p_" + cache.current.themeHash + ":" + rule.name + ":" + ObjectUtils_1.ObjectUtils.keyFromObject(props);
        }
        else if (typeof key === "string") {
            key = "_k_" + cache.current.themeHash + ":" + rule.name + ":" + key;
        }
        else {
            key =
                "_a_" +
                    cache.current.themeHash +
                    ":" +
                    rule.name +
                    ":" +
                    key
                        .filter((k) => props[k] != null)
                        .map((k) => `${k}${ObjectUtils_1.ObjectUtils.keyFromObject(props[k])}`)
                        .join("-");
        }
        return key;
    }
    function cachedClass(rule, props = {}, key) {
        if (enabled) {
            key = "_cc_" + makeKey(rule, props, key);
            // console.log(`cachedClass: made key ${key}`);
            if (cache.current.cache[cache.current.themeHash][key]) {
                return cache.current.cache[cache.current.themeHash][key];
            }
            const classes = renderer.renderRule(rule, { theme: cache.current.theme, ...props });
            cache.current.cache[cache.current.themeHash][key] = classes;
            return classes;
        }
        return renderer.renderRule(rule, { theme: cache.current.theme, ...props });
    }
    function cachedKeyframe(rule, props = {}, key) {
        if (enabled) {
            key = "_kf_" + makeKey(rule, props, key);
            if (cache.current.cache[cache.current.themeHash][key]) {
                return cache.current.cache[cache.current.themeHash][key];
            }
            const keyframeKey = renderer.renderKeyframe(rule, { theme: cache.current.theme, ...props });
            cache.current.cache[cache.current.themeHash][key] = keyframeKey;
            return keyframeKey;
        }
        return renderer.renderKeyframe(rule, { theme: cache.current.theme, ...props });
    }
    function inlineCss(cssRules) {
        if (cssRules.length > 0) {
            return css(...cssRules);
        }
        return "";
    }
    if (cache.current === undefined || cache.current.themeHash !== themeHash) {
        if (cache.current != null) {
            cache.current.themeHash = themeHash;
            cache.current.theme = theme;
            if (cache.current.cache[themeHash] == null) {
                cache.current.cache[themeHash] = {};
            }
        }
        else {
            cache.current = {
                cache: { [themeHash]: {} },
                cachedClass,
                cachedKeyframe,
                inlineCss,
                themeHash,
                theme,
            };
        }
    }
    return (0, jsx_runtime_1.jsx)(Provider, { value: cache.current, children: children });
};
exports.FelaCacheProvider = FelaCacheProvider;
function useFelaCache() {
    const { theme } = (0, react_fela_1.useFela)();
    const cacheContext = (0, react_1.useContext)(felaCacheContext);
    return {
        cachedClass: cacheContext.cachedClass,
        cachedKeyframe: cacheContext.cachedKeyframe,
        inlineCss: cacheContext.inlineCss,
        theme,
    };
}
